/**
 * Socket Service for Real-Time Chat
 * Handles WebSocket connection and events for buddy chat system
 */

import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.isInitialized = false;
  }

  /**
   * Initialize and connect to WebSocket server
   * @param {string} token - JWT authentication token
   * @returns {boolean} - Connection success status
   */
  connect(token) {
    if (!token) {
      console.error('[SocketService] No auth token provided');
      return false;
    }

    if (this.isInitialized && this.socket?.connected) {
      console.log('[SocketService] Already connected');
      return true;
    }

    try {
      // Determine backend URL based on environment
      let apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      // Remove /api suffix if present - Socket.io needs the root URL
      const backendUrl = apiUrl.replace(/\/api\/?$/, '');

      // Create socket and return a promise that resolves on connection
      return new Promise((resolve) => {
        try {
          this.socket = io(backendUrl, {
            auth: { token: token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000
          });

          this.setupEventHandlers();
          this.isInitialized = true;

          console.log('[SocketService] Connecting to:', backendUrl);

          const onConnect = () => {
            this.socket.off('connect_error', onError);
            resolve(true);
          };

          const onError = (err) => {
            console.error('[SocketService] connect_error:', err?.message || err);
            this.socket.off('connect', onConnect);
            resolve(false);
          };

          this.socket.once('connect', onConnect);
          this.socket.once('connect_error', onError);
        } catch (error) {
          console.error('[SocketService] Connection error:', error);
          resolve(false);
        }
      });

    } catch (error) {
      console.error('[SocketService] Connection error:', error);
      return false;
    }
  }

  /**
   * Setup default event handlers
   */
  setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('[SocketService] ✓ Connected - SID:', this.socket.id);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[SocketService] Disconnected:', reason);
      if (reason === 'io server disconnect') {
        // Server disconnected, reconnect manually
        this.socket.connect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('[SocketService] Connection error:', error.message);
    });

    this.socket.on('error', (error) => {
      console.error('[SocketService] Socket error:', error);
    });

    this.socket.on('connected', (data) => {
      console.log('[SocketService] Authenticated as user:', data.user_id);
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      console.log('[SocketService] Disconnecting...');
      this.socket.disconnect();
      this.socket = null;
      this.isInitialized = false;
      this.listeners.clear();
    }
  }

  /**
   * Check if socket is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.socket && this.socket.connected;
  }

  // ===================================
  // CHAT ROOM MANAGEMENT
  // ===================================

  /**
   * Join a chat room for a specific match
   * @param {number} matchId - The match ID
   */
  joinChat(matchId) {
    if (!this.socket) {
      console.error('[SocketService] Socket not initialized');
      return;
    }
    console.log('[SocketService] Joining chat:', matchId);
    this.socket.emit('join_chat', { match_id: matchId });
  }

  /**
   * Leave a chat room
   * @param {number} matchId - The match ID
   */
  leaveChat(matchId) {
    if (!this.socket) return;
    console.log('[SocketService] Leaving chat:', matchId);
    this.socket.emit('leave_chat', { match_id: matchId });
  }

  // ===================================
  // MESSAGING
  // ===================================

  /**
   * Send a message broadcast event
   * @param {number} matchId - The match ID
   * @param {object} payload - Must include { message_id } for backend broadcast
   */
  sendMessage(matchId, payload) {
    if (!this.socket) {
      console.error('[SocketService] Socket not initialized');
      return;
    }

    const messageData = {
      match_id: matchId,
      ...payload
    };

    console.log('[SocketService] Sending message event:', messageData);
    this.socket.emit('send_message', messageData);
  }

  // ===================================
  // TYPING INDICATORS
  // ===================================

  /**
   * Indicate that user started typing
   * @param {number} matchId - The match ID
   */
  startTyping(matchId) {
    if (!this.socket) return;
    this.socket.emit('typing_start', { match_id: matchId });
  }

  /**
   * Indicate that user stopped typing
   * @param {number} matchId - The match ID
   */
  stopTyping(matchId) {
    if (!this.socket) return;
    this.socket.emit('typing_stop', { match_id: matchId });
  }

  // ===================================
  // READ RECEIPTS
  // ===================================

  /**
   * Mark a message as read
   * @param {number} matchId - The match ID
   * @param {number} messageId - The message ID
   */
  markAsRead(matchId, messageId) {
    if (!this.socket) return;
    this.socket.emit('mark_read', { match_id: matchId, message_id: messageId });
  }

  // ===================================
  // EVENT LISTENERS
  // ===================================

  /**
   * Register an event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   */
  on(event, callback) {
    if (!this.socket) {
      console.error('[SocketService] Socket not initialized');
      return;
    }

    this.socket.on(event, callback);

    // Store for cleanup
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Remove a specific event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function to remove
   */
  off(event, callback) {
    if (!this.socket) return;

    this.socket.off(event, callback);

    // Remove from stored listeners
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Remove all listeners for an event
   * @param {string} event - Event name
   */
  removeAllListeners(event) {
    if (!this.socket) return;
    this.socket.off(event);
    this.listeners.delete(event);
  }

  /**
   * Get the socket instance (use with caution)
   * @returns {Socket}
   */
  getSocket() {
    return this.socket;
  }
}

// Create and export singleton instance
const socketService = new SocketService();

export default socketService;
